import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface TutorialState {
  showTutorial: boolean;
  tutorialStep: number;
  hasProperties: boolean;
  investorType: string | null;
}

export function useTutorial() {
  const location = useLocation();
  const [tutorialState, setTutorialState] = useState<TutorialState>({
    showTutorial: false,
    tutorialStep: 0,
    hasProperties: false,
    investorType: null
  });

  // Check if user has properties and get investor type
  const checkUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get customer data including investor type
      const { data: customerData, error: customerError } = await supabase
        .from('kunden')
        .select('id, investor_type')
        .eq('auth_user_id', user.id)
        .single();

      if (customerError) throw customerError;
      if (!customerData) return;

      // Check if user has properties
      const { count, error: propertiesError } = await supabase
        .from('immobilien')
        .select('*', { count: 'exact' })
        .eq('kunde_id', customerData.id);

      if (propertiesError) throw propertiesError;

      const hasProperties = (count || 0) > 0;
      const shouldShowTutorial = !hasProperties && customerData.investor_type;

      setTutorialState(prev => ({
        ...prev,
        hasProperties,
        investorType: customerData.investor_type,
        showTutorial: shouldShowTutorial,
        tutorialStep: shouldShowTutorial ? 0 : prev.tutorialStep
      }));

    } catch (error) {
      console.error('Error checking user data:', error);
    }
  };

  // Initialize tutorial check
  useEffect(() => {
    checkUserData();
  }, []);

  // Handle tutorial step progression
  const nextTutorialStep = () => {
    setTutorialState(prev => ({
      ...prev,
      tutorialStep: prev.tutorialStep + 1
    }));
  };

  const closeTutorial = () => {
    setTutorialState(prev => ({
      ...prev,
      showTutorial: false,
      tutorialStep: 0
    }));
  };

  // Auto-advance tutorial based on location
  useEffect(() => {
    if (tutorialState.showTutorial && tutorialState.tutorialStep === 0 && location.pathname === '/portfoliopage') {
      nextTutorialStep();
    }
  }, [location.pathname, tutorialState.showTutorial, tutorialState.tutorialStep]);

  return {
    ...tutorialState,
    nextTutorialStep,
    closeTutorial,
    refreshUserData: checkUserData
  };
}